import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"

// Connects to data-controller="markdown"
// This controller watches for changes to raw markdown content and renders it
export default class extends Controller {
  static targets = ["raw", "rendered"]
  static values = { baseUrl: String }

  connect() {
    // Configure marked for better rendering
    marked.setOptions({
      breaks: true,        // Convert \n to <br>
      gfm: true,           // GitHub Flavored Markdown
      headerIds: false,    // Don't add IDs to headers
      mangle: false,       // Don't mangle email addresses
      sanitize: false      // Allow HTML (marked escapes by default in newer versions)
    })

    // Set up a MutationObserver to watch for content changes
    this.observer = new MutationObserver(() => {
      this.render()
    })

    // Observe changes to the raw content target
    if (this.hasRawTarget) {
      this.observer.observe(this.rawTarget, {
        childList: true,      // Watch for added/removed nodes
        characterData: true,  // Watch for text changes
        subtree: true         // Watch all descendants
      })
    }

    // Initial render
    this.render()
  }

  disconnect() {
    // Clean up the observer when controller disconnects
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  render() {
    if (!this.hasRawTarget || !this.hasRenderedTarget) return
    
    // Get all the raw markdown text content
    const rawMarkdown = this.rawTarget.textContent.trim()
    
    if (!rawMarkdown) {
      this.renderedTarget.innerHTML = ''
      return
    }

    // Parse and render the markdown
    const html = marked.parse(rawMarkdown)

    // Update the rendered target with HTML
    this.renderedTarget.innerHTML = html

    // Process all links
    this.renderedTarget.querySelectorAll('a').forEach(link => {
      const href = link.getAttribute('href')

      // Convert relative links to absolute using the base URL if provided
      if (href && this.hasBaseUrlValue && this.baseUrlValue) {
        try {
          const baseUrl = new URL(this.baseUrlValue)
          if (href.startsWith('/')) {
            // Absolute path relative to domain (e.g., /kite)
            link.setAttribute('href', `${baseUrl.origin}${href}`)
          } else if (!href.startsWith('http') && !href.startsWith('mailto:') && !href.startsWith('#')) {
            // Relative path (e.g., kite or ../kite)
            link.setAttribute('href', new URL(href, this.baseUrlValue).href)
          }
        } catch (e) {
          // Invalid URL, leave as-is
        }
      }

      // Make all links open in new tab (important for iframe contexts)
      link.setAttribute('target', '_blank')
      link.setAttribute('rel', 'noopener noreferrer')
    })
  }
}

